<template>
  <component-wrapper
    :class="{
      'horizontal-banner-fi': true,
      'horizontal-banner-fi--exp': isEditMode,
      'horizontal-banner-fi--exp-has-image':
        isEditMode && !fields.image?.value?.src?.includes('default_image.svg'),
      'horizontal-banner-fi--exp-dark': isEditMode && isDark && !hasdoNotModifyImage,
      'horizontal-banner-fi--exp-light': isEditMode && isLight && !hasdoNotModifyImage,
    }"
  >
    <component
      :is="isLink ? 'a' : 'div'"
      v-bind="isLink ? linkAttrs : null"
    >
      <div class="horizontal-banner-fi__text-container">
        <grid-container-fi class="horizontal-banner-fi__text-container__grid">
          <div
            :class="{
              'horizontal-banner-fi__right-banner-text': Boolean(fields.textRight?.value),
              'horizontal-banner-fi__banner-text': !Boolean(fields.textRight?.value),
            }"
          >
            <richtext-fi
              :is-theme-light="Boolean(fields.mode?.value === 'dark')"
              field-name="content"
            />
            <link-field-fi
              v-if="inlineEditMode || isEditMode"
              field-name="link"
              class="fi-text-size-df-30"
            >
              <arrow-icon :class="arrowClass" />
            </link-field-fi>
            <arrow-icon
              v-else-if="link && hasContent"
              class="fi-text-size-df-30"
              :class="arrowClass"
            />
          </div>
        </grid-container-fi>
      </div>

      <div class="horizontal-banner-fi__banner-image">
        <div
          v-if="inlineEditMode"
          class="fi-absolute fi-top-0 fi-min-w-120 fi-max-w-[120px]"
          :class="fields.textRight?.value ? 'fi-left-0' : 'fi-right-0'"
        >
          <div v-if="fields.image?.value?.src">
            desktop:<br />
            <cloud-image-fi field-name="image" />
          </div>
          <div v-if="fields.imageTablet?.value?.src">
            tablet:<br />
            <cloud-image-fi field-name="imageTablet" />
          </div>
          <div v-if="fields.imageMobile?.value?.src">
            mobile:<br />
            <cloud-image-fi field-name="imageMobile" />
          </div>
        </div>
        <picture
          v-if="!isEditMode && fields.image?.value?.src"
          :key="isMounted ? 'dom' : 'ssr'"
        >
          <source
            media="(max-width: 767px)"
            v-bind="mobileDims"
            :srcset="
              appendParams(
                `${getProxySrc(fields.imageMobile?.value?.src || fields.image.value.src)}`,
                mobileImageParams,
              )
            "
          />
          <source
            media="(max-width: 1439px)"
            v-bind="tabletDims"
            :srcset="
              appendParams(
                `${getProxySrc(fields.imageTablet?.value?.src || fields.image.value.src)}`,
                tabletImageParam,
              )
            "
          />
          <img
            v-bind="desktopDims"
            :src="appendParams(`${getProxySrc(fields.image.value.src)}`, imageParam)"
            :alt="fields.image.value.alt"
          />
        </picture>
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-else-if="isEditMode"
          v-html="fields.image?.editable"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </component>
  </component-wrapper>
</template>

<script setup lang="ts">
import { ArrowIcon, CloudImageFi, LinkFieldFi, RichtextFi } from 'atoms';
import { computed, onMounted, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useCloudImage } from '@/composables/cloudImage';
import { useFischer } from '@/composables/fischerPlugin';
import { useInlineEdit } from '@/composables/inlineEdit';
import type {
  CheckboxField,
  DropDown,
  ImageField,
  LinkField,
  SingleLineTextField,
} from '@/sitecoreFieldTypes';

const props = withDefaults(
  defineProps<{
    fields?: {
      image?: ImageField;
      imageMobile?: ImageField;
      imageTablet?: ImageField;
      link?: LinkField;
      content?: SingleLineTextField;
      doNotModifyImage?: CheckboxField;
      mode?: DropDown;
      textRight?: CheckboxField;
    };
  }>(),
  {
    fields: () => ({}),
  },
);

const { getProxySrc, appendParams } = useCloudImage();
const { inlineEditMode } = useInlineEdit();
const fischer = useFischer();

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});

const isDark = computed<boolean>(() => {
  return props.fields?.mode?.value === 'dark';
});

const isLight = computed<boolean>(() => {
  return props.fields?.mode?.value === 'light';
});

const link = computed<string | null>(() => {
  if (inlineEditMode.value) {
    return null;
  }
  const url = props.fields?.link?.value?.href || props.fields?.link?.value?.url;
  if (url && url !== '') {
    return url;
  }
  return null;
});

const linkRel = computed(() => {
  const val = props.fields?.link?.value;
  if (val?.url && val.target === '_blank') {
    return 'noopener noreferrer';
  }
  return undefined;
});

/** The whole banner is the link, unless Sitecore's editor needs the markup to stay editable. */
const isLink = computed<boolean>(() => Boolean(link.value) && !fischer.app.isEditMode());

const linkAttrs = computed(() => ({
  href: link.value,
  target: props.fields?.link?.value?.target || undefined,
  rel: linkRel.value,
}));

const arrowClass = computed(() => ({
  'horizontal-banner-fi__banner-text__arrow': true,
  'horizontal-banner-fi__banner-text__arrow--dark': isLight.value,
}));

const hasdoNotModifyImage = computed<boolean>(() => {
  return !!props.fields?.doNotModifyImage?.value;
});

const hasContent = computed<boolean>(() => {
  const content = props.fields?.content?.value;
  if (!content) {
    return false;
  }
  return (
    content
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0
  );
});

const bgColorise = computed<string>(() => {
  if (props.fields?.doNotModifyImage?.value) {
    return '';
  } else if (isDark.value) {
    return '&colorize=black';
  } else {
    return '&colorize=white';
  }
});

/** One crop per breakpoint, matching the aspect ratios `.horizontal-banner-fi` itself switches to. */
interface ImageTier {
  height: number;
  ratio: [number, number];
  extra?: string;
}

const MOBILE_TIER: ImageTier = { height: 767, ratio: [16, 14], extra: '&gravity=north' };
const TABLET_TIER: ImageTier = { height: 720, ratio: [16, 6] };
const DESKTOP_TIER: ImageTier = { height: 480, ratio: [16, 4] };

const cropParams = (tier: ImageTier) =>
  `h=${tier.height}&aspect_ratio=${tier.ratio[0]}:${tier.ratio[1]}&func=crop&org_if_sml=0${
    tier.extra ?? ''
  }${bgColorise.value}`;

/**
 * Explicit dimensions on every `source` and on the `img` reserve the box before the bytes arrive -
 * without them the wrapper is 0px tall until the image loads, and again for a frame every time the
 * `key` above recreates the `picture` at hydration. Derived from the crop so the two cannot drift;
 * `org_if_sml=0` may deliver a smaller image, but never a different ratio.
 */
const tierDims = (tier: ImageTier) => ({
  width: Math.round((tier.height * tier.ratio[0]) / tier.ratio[1]),
  height: tier.height,
});

const mobileDims = tierDims(MOBILE_TIER);
const tabletDims = tierDims(TABLET_TIER);
const desktopDims = tierDims(DESKTOP_TIER);

const imageParam = computed<string>(() => cropParams(DESKTOP_TIER));
const tabletImageParam = computed<string>(() => cropParams(TABLET_TIER));
const mobileImageParams = computed<string>(() => cropParams(MOBILE_TIER));
</script>

<style scoped src="./horizontal-banner-fi" />

<style src="./horizontal-banner-fi-unscoped" />
